import React from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { useGymTimezone } from "../../../utils/use-gym-timezone";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { BookingsTableSkeleton } from "./SchedulingSkeletons";

const BOOKING_TYPE_LABELS = {
  class: "Classes",
  classes: "Classes",
  pt_session: "PT Session",
  pt_sessions: "PT Session",
  facility: "Facility",
  facilities: "Facility",
};

const BOOKING_STATUS_LABELS = {
  booked: "Confirmed",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  attended: "Attended",
  no_show: "No-Show",
  absent: "Absent",
  pending: "Pending",
};

const FILTER_TABS = [
  { id: "all", label: "All Bookings" },
  { id: "class", label: "Classes" },
  { id: "pt_session", label: "PT sessions" },
  { id: "facility", label: "Facilities" },
];

const BOOKINGS_PAGE_SIZE = 25;

function isValidTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

function formatBookingDateTime(iso, timeZone) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const timeOpts = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const dateOpts = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  if (isValidTimeZone(timeZone)) {
    timeOpts.timeZone = timeZone;
    dateOpts.timeZone = timeZone;
  }
  const time = d.toLocaleString("en-US", timeOpts);
  const date = d.toLocaleDateString("en-US", dateOpts);
  return `${time}, ${date}`;
}

function parseBookings(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.bookings)) return data.bookings;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function typeKey(raw) {
  return raw.toLowerCase().replace(/[^a-z_]/g, "_");
}

function statusKey(raw) {
  return raw.toLowerCase().replace(/[^a-z]/g, "_");
}

const SchedulingBookings = ({ hasNonPullScheduling = true }) => {
  const { locals } = useRillContext();
  const theme = locals.activeUser.webAppTheme || "dark";
  const fetchedTz = useGymTimezone();
  const gymTimezone = fetchedTz || locals?.activeUser?.timeZone || null;
  // Facilities are hidden when only PULL scheduling is enabled.
  const visibleFilterTabs = hasNonPullScheduling
    ? FILTER_TABS
    : FILTER_TABS.filter((t) => t.id !== "facility");
  const [bookings, setBookings] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(true);
  const [listError, setListError] = React.useState(null);
  const [activeFilter, setActiveFilter] = React.useState("all");
  const [filterPanelOpen, setFilterPanelOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState(() => new Set());
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [memberSearch, setMemberSearch] = React.useState("");
  const [pageSelection, setPageSelection] = React.useState({
    list: null,
    page: 1,
  });
  const filterPanelRef = React.useRef(null);
  const tableHeaderRef = React.useRef(null);

  React.useEffect(() => {
    if (!filterPanelOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (filterPanelRef.current?.contains(e.target)) return;
      setFilterPanelOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [filterPanelOpen]);

  const toggleStatusFilter = React.useCallback((status) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const clearFilters = React.useCallback(() => {
    setStatusFilter(new Set());
    setDateFrom("");
    setDateTo("");
    setMemberSearch("");
  }, []);

  const activeExtraFilterCount =
    statusFilter.size +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (memberSearch.trim() ? 1 : 0);

  const singleFilterLabel = React.useMemo(() => {
    if (activeExtraFilterCount !== 1) return null;
    if (statusFilter.size === 1) {
      const [only] = statusFilter;
      const labels = {
        booked: "Confirmed",
        cancelled: "Cancelled",
        attended: "Attended",
        absent: "Absent",
      };
      return labels[only] ?? only;
    }
    if (dateFrom) return `From ${dateFrom}`;
    if (dateTo) return `To ${dateTo}`;
    const q = memberSearch.trim();
    if (q) return q;
    return null;
  }, [activeExtraFilterCount, statusFilter, dateFrom, dateTo, memberSearch]);

  const loadBookings = React.useCallback(async () => {
    setListError(null);
    setListLoading(true);
    try {
      const res = await fetch("/actions/scheduling/coach/bookings", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        const msg =
          (typeof data?.error === "string" && data.error) ||
          (typeof data?.message === "string" && data.message) ||
          `Could not load bookings (${res.status})`;
        throw new Error(msg);
      }
      setBookings(parseBookings(data));
    } catch (e) {
      setListError(e?.message ?? "Could not load bookings");
      setBookings([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const filteredBookings = React.useMemo(() => {
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    const memberQ = memberSearch.trim().toLowerCase();
    return bookings.filter((b) => {
      const raw = String(b.event_type ?? b.type ?? "").toLowerCase();
      if (activeFilter !== "all") {
        if (activeFilter === "class" && !(raw === "class" || raw === "classes"))
          return false;
        if (
          activeFilter === "pt_session" &&
          !(raw === "pt_session" || raw === "pt_sessions")
        )
          return false;
        if (
          activeFilter === "facility" &&
          !(raw === "facility" || raw === "facilities")
        )
          return false;
      }
      if (statusFilter.size > 0) {
        const status = String(b.status ?? "").toLowerCase();
        const normalized = status === "canceled" ? "cancelled" : status;
        if (!statusFilter.has(normalized)) return false;
      }
      if (fromMs != null || toMs != null) {
        const raw = b.start_datetime ?? b.start_time ?? null;
        const ms = raw ? new Date(raw).getTime() : NaN;
        if (Number.isNaN(ms)) return false;
        if (fromMs != null && ms < fromMs) return false;
        if (toMs != null && ms > toMs) return false;
      }
      if (memberQ) {
        const name = String(b.member_name ?? "").toLowerCase();
        const email = String(b.member_email ?? "").toLowerCase();
        if (!name.includes(memberQ) && !email.includes(memberQ)) return false;
      }
      return true;
    });
  }, [bookings, activeFilter, statusFilter, dateFrom, dateTo, memberSearch]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredBookings.length / BOOKINGS_PAGE_SIZE)
  );
  const currentPage =
    pageSelection.list === filteredBookings
      ? Math.min(pageSelection.page, pageCount)
      : 1;
  const pageStart = (currentPage - 1) * BOOKINGS_PAGE_SIZE;
  const pageBookings = filteredBookings.slice(
    pageStart,
    pageStart + BOOKINGS_PAGE_SIZE
  );

  const goToPage = (page) => {
    setPageSelection({ list: filteredBookings, page });
    tableHeaderRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  };

  return (
    <div className="scheduling-bookings scheduling-classes">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">Bookings</h1>
          <p className="scheduling-classes-subtitle">
            View and manage all bookings across classes, PT sessions, and
            facilities.
          </p>
        </hgroup>
      </header>

      <div className="scheduling-bookings-filter-bar">
        <div className="scheduling-bookings-filter-tabs">
          {visibleFilterTabs.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className="scheduling-bookings-filter-tab"
              aria-current={activeFilter === id ? "page" : undefined}
              onClick={() => setActiveFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          ref={filterPanelRef}
          className={`scheduling-bookings-filter-wrap${filterPanelOpen ? " is-open" : ""}`}
          style={{ position: "relative", marginLeft: "auto" }}
        >
          <button
            type="button"
            className="scheduling-classes-filter-btn"
            aria-haspopup="dialog"
            aria-expanded={filterPanelOpen}
            onClick={() => setFilterPanelOpen((o) => !o)}
          >
            <img src="/img/new-icons/filter-white.svg" alt="" />
            {singleFilterLabel ?? "Filter"}
            {activeExtraFilterCount > 1 && (
              <i
                className="scheduling-overview-filter-badge"
                style={{ fontStyle: "normal" }}
              >
                {activeExtraFilterCount}
              </i>
            )}
          </button>
          {filterPanelOpen && (
            <div
              role="dialog"
              aria-label="Filter bookings"
              className="scheduling-bookings-filter-panel"
              style={{
                position: "absolute",
                top: "calc(100% + 0.35rem)",
                right: 0,
                zIndex: 50,
                minWidth: 280,
                padding: 16,
                background: "var(--dark-grey)",
                border: "1px solid var(--white-10)",
                borderRadius: "0.5rem",
                boxShadow:
                  "0 12px 32px color-mix(in srgb, #000 45%, transparent)",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div>
                <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Status</p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {[
                    { id: "booked", label: "Confirmed" },
                    { id: "cancelled", label: "Cancelled" },
                    { id: "attended", label: "Attended" },
                    { id: "absent", label: "Absent" },
                    { id: "no_show", label: "No-Show" },
                  ].map((s) => (
                    <label
                      key={s.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={statusFilter.has(s.id)}
                        onChange={() => toggleStatusFilter(s.id)}
                      />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Date range</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    <span style={{ fontSize: 12, opacity: 0.7 }}>From</span>
                    <input
                      type="date"
                      className="text-input"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </label>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      flex: 1,
                    }}
                  >
                    <span style={{ fontSize: 12, opacity: 0.7 }}>To</span>
                    <input
                      type="date"
                      className="text-input"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </label>
                </div>
              </div>
              <div>
                <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Member</p>
                <input
                  type="text"
                  className="text-input"
                  placeholder="Search by name or email"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
              </div>
              <footer
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  data-button-type="tertiary"
                  onClick={clearFilters}
                >
                  Clear all
                </button>
                <button
                  type="button"
                  data-button-type="primary"
                  onClick={() => setFilterPanelOpen(false)}
                >
                  Done
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>

      <div className="scheduling-bookings-box">
        {listLoading ? (
          <BookingsTableSkeleton theme={theme} />
        ) : listError ? (
          <p className="scheduling-classes-empty" role="alert">
            {listError}
          </p>
        ) : filteredBookings.length === 0 ? (
          <SchedulingEmptyState
            iconSrc="/img/new-icons/scheduling-calendar-icon.svg"
            title="No bookings yet"
            description="Bookings for your classes and PT sessions will appear here."
          />
        ) : (
          <div className="scheduling-bookings-table">
            <div
              ref={tableHeaderRef}
              className="scheduling-bookings-header scheduling-bookings-row"
            >
              <div className="scheduling-bookings-header-cell">Booking ID</div>
              <div className="scheduling-bookings-header-cell">
                Members Name
              </div>
              <div className="scheduling-bookings-header-cell">Event Name</div>
              <div className="scheduling-bookings-header-cell">Type</div>
              <div className="scheduling-bookings-header-cell">Time/date</div>
              <div className="scheduling-bookings-header-cell">Status</div>
            </div>
            <ul className="scheduling-bookings-list">
              {pageBookings.map((b) => {
                const id = String(b._id ?? b.id ?? "");
                const bookingRef = String(
                  b.booking_number ?? `BK#${id.slice(-4).toUpperCase()}`
                );
                const memberName = String(b.member_name ?? "—");
                const rawAvatar = b.member_avatar ?? null;
                const hasAvatarPath =
                  rawAvatar &&
                  typeof rawAvatar === "object" &&
                  typeof rawAvatar.s3Path === "string" &&
                  rawAvatar.s3Path.length > 0;
                const memberAvatarUrl = hasAvatarPath
                  ? getAvatarUrl({ avatar: rawAvatar }, false, theme)
                  : typeof rawAvatar === "string" && rawAvatar
                    ? rawAvatar
                    : null;
                const eventName = String(b.event_name ?? "—");
                const rawType = String(b.event_type ?? "");
                const typeLabel =
                  BOOKING_TYPE_LABELS[rawType.toLowerCase()] || rawType || "—";
                const rawStatus = String(b.status ?? "");
                const statusLabel =
                  BOOKING_STATUS_LABELS[rawStatus.toLowerCase()] ||
                  rawStatus ||
                  "—";
                const dateTime = formatBookingDateTime(
                  b.start_datetime ?? b.start_time,
                  b.event_timezone || gymTimezone
                );

                return (
                  <li
                    key={id || `${memberName}-${dateTime}`}
                    className="scheduling-bookings-row"
                  >
                    <span className="scheduling-bookings-cell scheduling-bookings-id">
                      {bookingRef}
                    </span>
                    <div className="scheduling-bookings-cell scheduling-bookings-member">
                      <div
                        className="scheduling-bookings-avatar"
                        aria-hidden="true"
                      >
                        {memberAvatarUrl ? (
                          <img src={memberAvatarUrl} alt="" />
                        ) : (
                          <span>{memberName.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <span>{memberName}</span>
                    </div>
                    <span className="scheduling-bookings-cell">
                      {eventName}
                    </span>
                    <div className="scheduling-bookings-cell">
                      <span
                        className={`scheduling-bookings-type-badge scheduling-bookings-type-${typeKey(rawType)}`}
                      >
                        {typeLabel}
                      </span>
                    </div>
                    <span className="scheduling-bookings-cell">{dateTime}</span>
                    <div className="scheduling-bookings-cell">
                      <span
                        className={`scheduling-bookings-status-badge scheduling-bookings-status-${statusKey(rawStatus)}`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
            {pageCount > 1 && (
              <nav
                className="scheduling-bookings-pagination"
                aria-label="Bookings pages"
              >
                <span className="scheduling-bookings-pagination-summary">
                  {pageStart + 1}-{pageStart + pageBookings.length} of{" "}
                  {filteredBookings.length} bookings
                </span>
                <div className="scheduling-bookings-pagination-controls">
                  <button
                    type="button"
                    className="scheduling-overview-nav-btn"
                    aria-label="Previous page"
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <img src="/img/new-icons/arrow-left.svg" alt="" />
                  </button>
                  <span>
                    Page {currentPage} of {pageCount}
                  </span>
                  <button
                    type="button"
                    className="scheduling-overview-nav-btn"
                    aria-label="Next page"
                    disabled={currentPage >= pageCount}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    <img src="/img/new-icons/arrow-right.svg" alt="" />
                  </button>
                </div>
              </nav>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulingBookings;
